/**
 * Permissions admin API client. Drives the Roles & Permissions UI.
 *
 * USE_MOCK toggles between in-browser mock state and real HTTP calls. The
 * mock branch is kept for offline / Playwright development; flip the
 * constant to true to run against in-memory state without a backend.
 *
 * Endpoint shapes match the backend (Clerk-backed):
 *   GET    /permissions/catalog                → resource catalog (public)
 *   GET    /permissions/roles                  → built-ins first (isSystem),
 *                                                then org-custom roles
 *   PATCH  /permissions/roles/:slug            body: { name?, description? }
 *   GET    /permissions/roles/:slug/grants     → { permissions: [slug, ...] }
 *                                                (built-in admin returns the
 *                                                 full catalog)
 *   PUT    /permissions/roles/:slug/grants     body: { permissions: [...] }
 *                                                (diff-applied vs Clerk role)
 *
 * User/membership management (listing members, assigning roles, invitations)
 * is handled by Clerk's native OrganizationProfile "Members" tab, so no
 * user/invitation endpoints are called from here.
 */

import http from 'helpers/requests';
import { mock } from './mockData';

const USE_MOCK = false;

// Tiny artificial delay so the mock feels like real network latency —
// surfaces loading states in the UI during development.
const MOCK_LATENCY_MS = 150;
const withLatency =
  (fn) =>
  async (...args) => {
    if (!USE_MOCK) return fn(...args);
    await new Promise((r) => setTimeout(r, MOCK_LATENCY_MS));
    return fn(...args);
  };

// The `http` helper returns raw axios responses, not unwrapped bodies. Every
// real-API call below ends in `.then(unwrap)` so consumers (react-query
// hooks, mock branches) see the parsed JSON directly.
const unwrap = (response) => response?.data;

// Effective permissions for the current Clerk user in the active org.
// Source of truth post-Mongo-pivot — Clerk JWT claims no longer carry the
// role/permission set, so `useAuth().has()` reads stale or empty data. The
// FE reads this once per session+org via PermissionsProvider and exposes a
// drop-in has() analogue. Returns:
//   { userId, orgId, isAdmin, roles: [bareSlug], permissions: [bareSlug] }
export const getMe = withLatency(async () => {
  if (USE_MOCK) return mock.getMe ? mock.getMe() : { isAdmin: false, roles: [], permissions: [] };
  return http.get('/permissions/me').then(unwrap);
});

export const listCatalog = withLatency(async () => {
  // The catalog endpoint is public-ish (available without permissions:manage)
  // so it's safe to call from anywhere. The mock branch doesn't model it —
  // every UI today reads the catalog from MATRIX_PAGES locally.
  if (USE_MOCK) return [];
  return http.get('/permissions/catalog').then(unwrap);
});

export const listRoles = withLatency(async () => {
  if (USE_MOCK) return mock.listRoles();
  return http.get('/permissions/roles').then(unwrap);
});

// Role creation and deletion are intentionally absent: roles are provisioned
// by Carbon Glance (backend/Clerk Dashboard), not self-serve from the app.

export const updateRole = withLatency(async (slug, patch) => {
  if (USE_MOCK) return mock.updateRole(slug, patch);
  return http.patch(`/permissions/roles/${encodeURIComponent(slug)}`, patch).then(unwrap);
});

export const getRoleGrants = withLatency(async (slug) => {
  if (USE_MOCK) return mock.getRoleGrants(slug);
  return http.get(`/permissions/roles/${encodeURIComponent(slug)}/grants`).then(unwrap);
});

export const updateRoleGrants = withLatency(async (slug, permissions) => {
  if (USE_MOCK) return mock.updateRoleGrants(slug, permissions);
  return http
    .put(`/permissions/roles/${encodeURIComponent(slug)}/grants`, { permissions })
    .then(unwrap);
});
